import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';

const compact = (v) => {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `$${n.toLocaleString()}`;
};

const px = 0.5;
const py = 0.25;
const cellSx = { px, py, fontSize: 11, whiteSpace: 'nowrap' };
const hdrSx = { ...cellSx, fontWeight: 700 };
const subHdrSx = { ...cellSx, fontWeight: 600 };

const stickyNum = { ...cellSx, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'inherit' };
const stickyName = { ...cellSx, position: 'sticky', left: 28, zIndex: 2, bgcolor: 'inherit', fontWeight: 600 };
const stickyNumHdr = { ...hdrSx, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'inherit' };
const stickyNameHdr = { ...hdrSx, position: 'sticky', left: 28, zIndex: 3, bgcolor: 'inherit' };

const br1 = { borderRight: '1px solid #ddd' };
const brInst = { borderRight: '2px solid #0d3c61' };
const brServ = { borderRight: '2px solid #7b1fa2' };
const brTot = { borderRight: '2px solid #00695c' };

const sum = (arr, key) => arr.reduce((s, r) => s + (Number(r[key]) || 0), 0);

const SalesmanTable = ({ salesmen, summary, conversionRate, totalRevenue, totalEquipment, totalPaid, netEarnings }) => (
  <Paper sx={{ p: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      <PeopleIcon sx={{ color: '#0d3c61', mr: 1, fontSize: 22 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d3c61', fontSize: 16 }}>Performance by Salesman</Typography>
    </Box>
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px, py } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...stickyNumHdr, ...br1, bgcolor: '#e8eaf6', minWidth: 28 }} rowSpan={2}>#</TableCell>
            <TableCell sx={{ ...stickyNameHdr, ...br1, bgcolor: '#e8eaf6', minWidth: 100 }} rowSpan={2}>Salesman</TableCell>
            <TableCell align="center" sx={{ ...hdrSx, ...br1, bgcolor: '#e8eaf6' }} rowSpan={2}>Crt</TableCell>
            <TableCell align="center" sx={{ ...hdrSx, ...br1, bgcolor: '#e8eaf6' }} rowSpan={2}>Vwd</TableCell>
            <TableCell align="center" sx={{ ...hdrSx, ...br1, bgcolor: '#e8eaf6' }} rowSpan={2}>Sgn</TableCell>
            <TableCell align="center" sx={{ ...hdrSx, ...brInst, bgcolor: '#e8eaf6' }} rowSpan={2}>%</TableCell>
            <TableCell colSpan={6} align="center" sx={{ ...hdrSx, ...brServ, bgcolor: '#e3f2fd', color: '#0d3c61', py: 0.3, borderBottom: '1px solid #ccc' }}>Installation</TableCell>
            <TableCell colSpan={5} align="center" sx={{ ...hdrSx, ...brTot, bgcolor: '#f3e5f5', color: '#7b1fa2', py: 0.3, borderBottom: '1px solid #ccc' }}>Service</TableCell>
            <TableCell colSpan={4} align="center" sx={{ ...hdrSx, bgcolor: '#e0f2f1', color: '#00695c', py: 0.3, borderBottom: '1px solid #ccc' }}>Total</TableCell>
          </TableRow>
          <TableRow>
            {[
              { l: 'Q', c: '#0d3c61' }, { l: 'V', c: '#0d3c61' }, { l: 'S', c: '#0d3c61' }, { l: 'C', c: '#0d3c61' }, { l: 'Val', c: '#0d3c61', a: 'right' },
              { l: 'Paid', c: '#0d3c61', a: 'right', sx: brServ },
              { l: 'Q', c: '#7b1fa2' }, { l: 'V', c: '#7b1fa2' }, { l: 'S', c: '#7b1fa2' }, { l: 'Val', c: '#7b1fa2', a: 'right' },
              { l: 'Paid', c: '#7b1fa2', a: 'right', sx: brTot },
              { l: 'Eqp', c: '#00695c', a: 'right' }, { l: 'Val', c: '#00695c', a: 'right' }, { l: 'Paid', c: '#00695c', a: 'right' }, { l: 'P-E', c: '#00695c', a: 'right' },
            ].map(({ l, c, a, sx: extra }, idx) => (
              <TableCell key={idx} align={a || 'center'} sx={{ ...subHdrSx, color: c, bgcolor: '#f5f5f5', ...(extra || {}) }}>{l}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {salesmen.map((row, i) => {
            const conv = row.total > 0 ? ((row.signed / row.total) * 100).toFixed(0) : 0;
            const bg = i % 2 === 0 ? '#fff' : '#fafafa';
            return (
              <TableRow key={row.salesmanId} hover sx={{ bgcolor: bg }}>
                <TableCell sx={{ ...stickyNum, ...br1, bgcolor: bg }}>{i + 1}</TableCell>
                <TableCell sx={{ ...stickyName, ...br1, bgcolor: bg }}>{row.salesmanName}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, ...br1 }}>{row.total}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, ...br1, color: '#e65100' }}>{row.viewed}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, ...br1, color: '#2e7d32', fontWeight: 600 }}>{row.signed}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, ...brInst, fontWeight: 600 }}>{conv}%</TableCell>
                <TableCell align="center" sx={{ ...cellSx, color: '#0d3c61' }}>{row.installTotal || 0}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, color: '#e65100' }}>{row.installViewed || 0}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, color: '#2e7d32' }}>{row.installSigned || 0}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, color: '#1565c0' }}>{row.installCompleted || 0}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: '#0d3c61' }}>{compact(row.installRevenue)}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: '#0d3c61', fontWeight: 600, ...brServ }}>{compact(row.installPaid)}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, color: '#7b1fa2' }}>{row.serviceTotal || 0}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, color: '#e65100' }}>{row.serviceViewed || 0}</TableCell>
                <TableCell align="center" sx={{ ...cellSx, color: '#2e7d32' }}>{row.serviceSigned || 0}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: '#7b1fa2' }}>{compact(row.serviceRevenue)}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: '#7b1fa2', fontWeight: 600, ...brTot }}>{compact(row.servicePaid)}</TableCell>
                <TableCell align="right" sx={{ ...cellSx }}>{compact(row.equipment)}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, color: '#1976d2' }}>{compact(row.revenue)}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, color: '#2e7d32' }}>{compact(row.paid)}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, color: '#00695c' }}>{compact(Number(row.paid || 0) - Number(row.paidEquipment || 0))}</TableCell>
              </TableRow>
            );
          })}
          {salesmen.length > 0 && (
            <TableRow sx={{ bgcolor: '#e8eaf6' }}>
              <TableCell sx={{ ...stickyNum, ...br1, bgcolor: '#e8eaf6' }} />
              <TableCell sx={{ ...stickyName, ...br1, bgcolor: '#e8eaf6', fontWeight: 700 }}>TOTAL</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, ...br1 }}>{summary?.total || 0}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, ...br1, color: '#e65100' }}>{summary?.viewedByClient || 0}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, ...br1, color: '#2e7d32' }}>{summary?.signed || 0}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, ...brInst }}>{conversionRate}%</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, color: '#0d3c61' }}>{sum(salesmen, 'installTotal')}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, color: '#e65100' }}>{sum(salesmen, 'installViewed')}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, color: '#2e7d32' }}>{sum(salesmen, 'installSigned')}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, color: '#1565c0' }}>{sum(salesmen, 'installCompleted')}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx, color: '#0d3c61' }}>{compact(sum(salesmen, 'installRevenue'))}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx, color: '#0d3c61', ...brServ }}>{compact(sum(salesmen, 'installPaid'))}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, color: '#7b1fa2' }}>{sum(salesmen, 'serviceTotal')}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, color: '#e65100' }}>{sum(salesmen, 'serviceViewed')}</TableCell>
              <TableCell align="center" sx={{ ...hdrSx, color: '#2e7d32' }}>{sum(salesmen, 'serviceSigned')}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx, color: '#7b1fa2' }}>{compact(sum(salesmen, 'serviceRevenue'))}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx, color: '#7b1fa2', ...brTot }}>{compact(sum(salesmen, 'servicePaid'))}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx }}>{compact(totalEquipment)}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx, color: '#1976d2' }}>{compact(totalRevenue)}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx, color: '#2e7d32' }}>{compact(totalPaid)}</TableCell>
              <TableCell align="right" sx={{ ...hdrSx, color: '#00695c' }}>{compact(netEarnings)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

export default SalesmanTable;
